import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MATERIAL_TABLE_GLOBAL_DENSE_STORAGE_KEY,
  MATERIAL_TABLE_DENSE_SYNC_EVENT,
  DENSE_MODE_APPLY_INDICATOR_MS,
  readGlobalDenseModeEnabled,
} from '../constants/globalDenseMode';

/**
 * Global dense table layout (localStorage + cross-component sync).
 * Use in DataGridPro, LTL Dashboard native tables, etc.
 */
export function useGlobalDenseMode() {
  const [denseMode, setDenseMode] = useState(readGlobalDenseModeEnabled);
  const [denseModeTogglePending, setDenseModeTogglePending] = useState(false);
  const denseToggleEndTimerRef = useRef(null);

  const scheduleDenseModeApplyIndicatorEnd = useCallback(() => {
    setDenseModeTogglePending(true);
    if (denseToggleEndTimerRef.current) {
      clearTimeout(denseToggleEndTimerRef.current);
    }
    denseToggleEndTimerRef.current = window.setTimeout(() => {
      setDenseModeTogglePending(false);
      denseToggleEndTimerRef.current = null;
    }, DENSE_MODE_APPLY_INDICATOR_MS);
  }, []);

  const syncDenseModeFromStorage = useCallback(() => {
    setDenseMode(readGlobalDenseModeEnabled());
    scheduleDenseModeApplyIndicatorEnd();
  }, [scheduleDenseModeApplyIndicatorEnd]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === MATERIAL_TABLE_GLOBAL_DENSE_STORAGE_KEY || e.key === null) {
        syncDenseModeFromStorage();
      }
    };
    const onSync = () => syncDenseModeFromStorage();
    window.addEventListener('storage', onStorage);
    window.addEventListener(MATERIAL_TABLE_DENSE_SYNC_EVENT, onSync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(MATERIAL_TABLE_DENSE_SYNC_EVENT, onSync);
      if (denseToggleEndTimerRef.current) {
        clearTimeout(denseToggleEndTimerRef.current);
        denseToggleEndTimerRef.current = null;
      }
    };
  }, [syncDenseModeFromStorage]);

  const persistDenseMode = useCallback((checked) => {
    try {
      localStorage.setItem(MATERIAL_TABLE_GLOBAL_DENSE_STORAGE_KEY, String(checked));
    } catch (error) {
      console.error('Failed to save dense mode:', error);
      return false;
    }
    window.dispatchEvent(new Event(MATERIAL_TABLE_DENSE_SYNC_EVENT));
    return true;
  }, []);

  return { denseMode, denseModeTogglePending, persistDenseMode };
}
